import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import React, { useEffect, useState } from 'react';

import {
  CCInfoContainer,
  CardCvcContainer,
  CardExpirationContainer,
  CardInputWrapper,
  CardNumberContainer,
  ExpirationAndCvcContainer,
  CreditCardDetailsContainer
} from './CreditCardDetails.styled';
import { Plus } from 'assets/icons/Icons';
import { StripeContext } from 'components/CreditCardForm/types/creditCardForm';
import { Loader } from 'components/Loader/Loader';
import { stripeInputStyle } from 'components/TextInput/NewTextInput';
import { Button } from 'components/v2/Buttons/Button/Button';
import { SelectField } from 'components/v2/Form/SelectField/SelectField';
import { Option } from 'components/v2/Inputs/SelectInput/SelectInput.types';
import { Text } from 'components/v2/Typography/Text';
import { useCreditCards } from 'hooks/useCreditCards/useCreditCards';
import { useFeatureFlags } from 'hooks/useFeatureFlags/useFeatureFlags';

import { ElementsContentProps, Props, CreditCardDisplayOptions } from './CreditCardDetails.types';

export const CreditCardDetails = ({
  onStripeCardElementInitialized,
  onStripeElementUnmount,
  onSelectCreditCard,
  onValidation,
  alwaysShowNewCreditCardForm = false,
  paymentOptional,
  onCreditCardChange,
  children
}: Props): React.JSX.Element => {
  const [selectedCreditCard, setSelectedCreditCard] = useState<Option>();
  const [creditCardOptions, setCreditCardOptions] = useState<Option[]>([]);
  const { showNewCreditCardForm, setShowNewCreditCardForm } = useNewCreditCardForm(
    alwaysShowNewCreditCardForm
  );

  const [stripePromise, setStripePromise] = useState<Promise<any> | null>(null);
  const stripePublicKey = import.meta.env.VITE_APP_KB_STRIPE_PLATFORM_ACCOUNT_PUBLIC_API_KEY;

  const { ppCreditCardsReuse } = useFeatureFlags();
  const { creditCards, creditCardsLoading } = useCreditCards();

  const preselectedCreditCardRemovable = paymentOptional && selectedCreditCard;

  useEffect(() => {
    onValidation(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (stripePublicKey && stripePublicKey.length > 0)
      setStripePromise(loadStripe(stripePublicKey));
  }, [stripePublicKey]);

  useEffect(() => {
    const options = creditCards.map(card => ({
      label: `**** **** **** ${card.lastFourDigits}`,
      value: card.stripeIdentifier
    }));
    setCreditCardOptions(options);

    const defaultCreditCard = creditCards.reduce<Option | undefined>((total, item) => {
      if (item.default) {
        return {
          label: `**** **** **** ${item.lastFourDigits}`,
          value: item.stripeIdentifier
        };
      }

      return total;
    }, undefined);
    setSelectedCreditCard(defaultCreditCard);
    if (creditCards.length > 0) {
      setShowNewCreditCardForm(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creditCards]);

  useEffect(() => {
    if (!ppCreditCardsReuse || !onSelectCreditCard) {
      return;
    }

    if (selectedCreditCard) {
      setShowNewCreditCardForm(false);
      onValidation(true);
    }

    onSelectCreditCard(selectedCreditCard?.value || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCreditCard, ppCreditCardsReuse]);

  useEffect(() => {
    if (showNewCreditCardForm) {
      setSelectedCreditCard(undefined);
      onValidation(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showNewCreditCardForm]);

  if (!stripePromise || creditCardsLoading) return <Loader width={50} height={50} container />;

  let displayOption: CreditCardDisplayOptions = 'new';

  if (showNewCreditCardForm && creditCards.length) {
    displayOption = 'newAndExisting';
  } else if (!showNewCreditCardForm && preselectedCreditCardRemovable) {
    displayOption = 'existingOptional';
  } else if (!showNewCreditCardForm) {
    displayOption = 'existingRequired';
  }

  const unselectCard = () => {
    onValidation(false);
    onCreditCardChange?.({ empty: true });
    setSelectedCreditCard(undefined);
  };

  let changeFormViewButton = null;

  // If there are existing credit cards, show the button to go to the existing CC selection
  if (displayOption === 'newAndExisting') {
    changeFormViewButton = (
      <Button
        type="button"
        size="lg"
        category="secondary"
        label="Use existing card"
        onClick={() => {
          setShowNewCreditCardForm(false);
          onStripeElementUnmount?.();
        }}
      />
    );
  } else if (displayOption === 'existingOptional') {
    changeFormViewButton = (
      <Button
        type="button"
        size="lg"
        category="secondary"
        label="Unselect card"
        onClick={() => {
          unselectCard();
          onStripeElementUnmount?.();
        }}
      />
    );
  } else if (displayOption === 'existingRequired') {
    changeFormViewButton = (
      <Button
        type="button"
        size="lg"
        category="secondary"
        label="New Card"
        onClick={() => {
          unselectCard();
          setShowNewCreditCardForm(true);
          onStripeElementUnmount?.();
        }}
        leftIcon={<Plus />}
      />
    );
  }

  return (
    <CreditCardDetailsContainer>
      {showNewCreditCardForm ? (
        <Elements stripe={stripePromise}>
          <ElementsContent
            onStripeCardElementInitialized={onStripeCardElementInitialized}
            onValidation={onValidation}
            paymentOptional={paymentOptional}
            onCreditCardChange={onCreditCardChange}
          />
        </Elements>
      ) : (
        <SelectField
          options={creditCardOptions}
          selected={selectedCreditCard}
          label={`Use existing card${paymentOptional ? ' (Optional)' : ''}`}
          placeholder="Select one"
          onChange={setSelectedCreditCard}
          id="credit-card-select-id"
          hideHelperText
        />
      )}

      {changeFormViewButton}

      {children}
    </CreditCardDetailsContainer>
  );
};

export const useNewCreditCardForm = (alwaysShowNewCreditCardForm: boolean) => {
  const [showNewCreditCardForm, setShowNewCreditCardForm] = useState<boolean>(true);

  if (alwaysShowNewCreditCardForm) {
    return {
      showNewCreditCardForm,
      setShowNewCreditCardForm: () => setShowNewCreditCardForm(true)
    };
  }

  return { showNewCreditCardForm, setShowNewCreditCardForm };
};

const ElementsContent = ({
  onStripeCardElementInitialized,
  onValidation,
  paymentOptional,
  onCreditCardChange
}: ElementsContentProps) => {
  const [cardNumberValid, setCardNumberValid] = useState(false);
  const [cardExpiryValid, setCardExpiryValid] = useState(false);
  const [cardCvcValid, setCardCvcValid] = useState(false);
  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    if (!stripe || !elements) return;

    const cardNumberElement = elements.getElement(CardNumberElement);
    if (cardNumberElement) onStripeCardElementInitialized(stripe, cardNumberElement);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripe, elements]);

  useEffect(() => {
    onValidation(cardNumberValid && cardExpiryValid && cardCvcValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardNumberValid, cardExpiryValid, cardCvcValid]);

  const options = {
    style: stripeInputStyle
  };

  const onElementChanged = (
    ev:
      | StripeContext['cardNumberChangeEvent']
      | StripeContext['cardExpiryChangeEvent']
      | StripeContext['cardCvcChangeEvent']
  ) => {
    const changedElementIsValid = !!(ev?.complete && !ev?.empty);

    switch (ev?.elementType) {
      case 'cardNumber':
        if (onCreditCardChange) {
          onCreditCardChange({ empty: ev?.empty });
        }
        return setCardNumberValid(changedElementIsValid);
      case 'cardExpiry':
        return setCardExpiryValid(changedElementIsValid);
      case 'cardCvc':
        return setCardCvcValid(changedElementIsValid);
      default:
        return;
    }
  };

  return (
    <CCInfoContainer>
      <CardNumberContainer>
        <Text tag="p">Card number{paymentOptional ? ' (Optional)' : ''}</Text>
        <CardInputWrapper>
          <CardNumberElement options={options} onChange={onElementChanged} />
        </CardInputWrapper>
      </CardNumberContainer>

      <ExpirationAndCvcContainer>
        <CardExpirationContainer>
          <Text tag="p">Expiration</Text>
          <CardInputWrapper>
            <CardExpiryElement
              options={{ ...options, placeholder: 'MM/YY' }}
              onChange={onElementChanged}
            />
          </CardInputWrapper>
        </CardExpirationContainer>

        <CardCvcContainer>
          <Text tag="p">CVC</Text>
          <CardInputWrapper>
            <CardCvcElement options={options} onChange={onElementChanged} />
          </CardInputWrapper>
        </CardCvcContainer>
      </ExpirationAndCvcContainer>
    </CCInfoContainer>
  );
};
